<script setup lang="ts">
//#region import
import { ref, computed, watch } from 'vue';
import { isEmpty, pick } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { useTranslateLanguages } from '@/composables/useTranslateLanguages';
import UploadFile from '@/components/UploadFile.vue';
import { type UploadedFile, type Asset , type EmitsToListeners, type ErrorResponse, typeGuard, type ToErrorMap } from '@/types/core';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import type {
  GetCurrenciesResponse,
  PutControlTowerPaymentMethodsByIdData,
} from '@/types/openapi-ts';
//#endregion

//#region types
type FileAttachmentOption = 'no' | 'allowed' | 'required';

interface DescriptionItem {
  language_code: string
  message: string
  is_default?: boolean
}

type GetControlTowerPaymentMethodsErrors = ToErrorMap<{
  files: any;
} & PutControlTowerPaymentMethodsByIdData['body']>;

export interface Props {
  id?: string | null
}

export interface  Emits {
  'close': [],
  'success': [],
};

export interface AddPaymentMethodParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  id: null,
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<GetControlTowerPaymentMethodsErrors>({});

const data = ref<PutControlTowerPaymentMethodsByIdData['body']>({
  name: '',
  supported_currencies: 'all',
  is_disabled: false,
  hidden: true,
  export_via_api: false,
  file_attachment_allowed: false,
  file_attachment_required: false,
  wallet_regexp: '',
  wallet_placeholder: '',
  required_affiliate_fields: [],
  currency_codes: [],
  min_payment_amount_currency_code: '',
  min_payment_amount: 0,
  description: {},
  logo_base64: '',
  other_properties: {},
});

const fileAttachments = ref<FileAttachmentOption>('no');
const paymentDescription = ref<DescriptionItem[]>([
  {
    language_code: 'en',
    message: '',
    is_default: true,
  },
]);
const minPaymentAmount = ref<number | null>(null);
const currencyCodes = ref<string[]>([]);
const files = ref<UploadedFile[]>([]);
const minAmountCurrencies = ref<GetCurrenciesResponse['payload']>([]);


const { selectedLanguages, defaultLanguageIndex, languages, index, handleSelectTab } = useTranslateLanguages({
  actions: computed(() => {
    return paymentDescription.value || [];
  }),
});
//#endregion

//#region computed
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const customerLanguages = computed(() => store.getters['misc/customerLanguages']);

const status = computed({
  get() {
    return !data.value.is_disabled;
  },
  set(newValue) {
    data.value.is_disabled = !newValue;
  },
});
const visibility = computed({
  get() {
    return !data.value.hidden;
  },
  set(newValue) {
    data.value.hidden = !newValue;
  },
});

const requiredAffiliateOptions = computed(() => {
  return [
    {
      id: 'phone_number',
      name: t('settings.paymentMethods.popup.requiredOptions.phone_number'),
    },
    {
      id: 'country_code',
      name: t('settings.paymentMethods.popup.requiredOptions.country_code'),
    },
    {
      id: 'address',
      name: t('settings.paymentMethods.popup.requiredOptions.address'),
    },
    {
      id: 'postal_code',
      name: t('settings.paymentMethods.popup.requiredOptions.postal_code'),
    },
  ];
});

const minimalPaymentAmountCurrencyOptions = computed( {
  get() {
    return data.value.supported_currencies === 'all' ? customerCurrencies.value : minAmountCurrencies.value;
  },
  set(newValue: string[]) {
    minAmountCurrencies.value = customerCurrencies.value.filter((item) => newValue.includes(item.code));
  },
});

const descriptionValue = computed( {
  get() {
    return paymentDescription.value?.[index.value]?.message || '';
  },
  set(newValue) {
    paymentDescription.value[index.value]!.message = newValue;
  },
});

const mapDescriptionErrors = computed(() => {
  return paymentDescription.value.map(({ language_code }) => errors?.value?.description?.[language_code] || null);
});

const fileAttachmentsOptions = computed(() => {
  return [
    {
      label: t('settings.paymentMethods.popup.no'),
      value: 'no',
    },
    {
      label: t('settings.paymentMethods.popup.allowed'),
      value: 'allowed',
    },
    {
      label: t('settings.paymentMethods.popup.required'),
      value: 'required',
    },
  ];
});

const supportedCurrenciesOptions = computed(() => {
  return [
    {
      label: t('settings.paymentMethods.popup.all'),
      value: 'all',
    },
    {
      label: t('settings.paymentMethods.popup.selected'),
      value: 'only_selected',
    },
  ];
});
//#endregion

//#region methods
const open = async () => {
  index.value = 0;
  isVisible.value = true;

  if (!props.id) return;

  isLoading.value = true;

  try {
    const { data: { payload } } = await api.getControlPaymentMethodsById(props.id);

    data.value = {
      ...pick(payload, [
        'name',
        'is_disabled',
        'hidden',
        'export_via_api',
        'file_attachment_allowed',
        'file_attachment_required',
        'wallet_regexp',
        'wallet_placeholder',
        'required_affiliate_fields',
        'min_payment_amount_currency_code',
        'min_payment_amount',
        'supported_currencies',
        'logo_base64',
      ]),
      currency_codes: [],
      description: {},
      other_properties: {},
    };

    if (data.value.file_attachment_allowed && data.value.file_attachment_required) {
      fileAttachments.value = 'required';
    } else if (data.value.file_attachment_allowed && !data.value.file_attachment_required) {
      fileAttachments.value = 'allowed';
    }

    const { description } = payload;
    paymentDescription.value = Object.entries({ en: '', ...description }).map(([language_code, message]) => ({
      language_code,
      message,
      is_default: language_code === 'en',
    })).sort((a, b) => {
      if (a.language_code === 'en') {
        return -1;
      }
      if (b.language_code === 'en') {
        return 1;
      }
      return a.language_code.localeCompare(b.language_code);
    });
    files.value = data.value.logo_base64 ? [{
      uid: 1,
      name: 'File uploaded',
      file: data.value.logo_base64,
    }] : [];
    minPaymentAmount.value = payload.min_payment_amount;
    currencyCodes.value = payload.currencies.length
      ? payload.currencies.map(({ code }) => code)
      : [];
  } finally {
    isLoading.value = false;
  }
};

const handleAddLanguage = (languageCode: string) => {
  paymentDescription.value.push({
    language_code: languageCode,
    message: '',
    is_default: false,
  });
  index.value = paymentDescription.value.length - 1;
};

const removeFile = () => {
  data.value.logo_base64 = '';
  files.value = [];
};

const getBase64 = (file: Blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

const uploadFile = (asset: Asset): Promise<void> => {
  if (!asset) return Promise.reject(new Error('File not found'));
  errors.value.files = undefined;
  if (asset.size > 100000) { // 100kb
    errors.value.files = [{ message: t('settings.paymentMethods.popup.limitFileSize') }];
    files.value = [];
    return Promise.reject(new Error('File size exceeds 100 KB'));
  }

  return getBase64(asset).then((res: any) => {
    files.value = [{
      uid: asset.uid,
      name: asset.name,
      file: res,
    }];
    data.value.logo_base64 = res;
  });
};

const handleDelete = () => {
  paymentDescription.value.splice(index.value, 1);
  index.value = defaultLanguageIndex.value;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};

const handleSave = () => {
  const params: PutControlTowerPaymentMethodsByIdData['body']  = {
    ...data.value,
  };

  const filteredDescription = paymentDescription.value
    .filter(({ message }) => !!message)
    .reduce((acc, { language_code, message }) => {
      acc[language_code] = message;
      return acc;
    }, {} as Record<string, string>);

  params.min_payment_amount = minPaymentAmount.value ? +minPaymentAmount.value : 0;
  params.description = isEmpty(filteredDescription) ? null : filteredDescription;
  params.currency_codes = currencyCodes.value;

  const promise = props.id
    ? api.putControlPaymentMethods(props.id, params)
    : api.postControlPaymentMethods(params);

  promise
    .then(() => {
      emit('success');
      handleClose();
    })
    .catch((e) => {
      if(typeGuard<ErrorResponse<GetControlTowerPaymentMethodsErrors>>(e)){
        errors.value = e?.data?.errors || {};
      }
    });
};
const preventSymbol = (e: KeyboardEvent) => {
  if (!(/^[0-9]*\.?[0-9]*$/).test(e.key) && ![8, 46].includes(e.keyCode)) {
    if(e.preventDefault) e.preventDefault();
  }
};
//#endregion

//#region watch
watch(() => currencyCodes.value, (newValue) => {
  minimalPaymentAmountCurrencyOptions.value = newValue;
  if (currencyCodes.value.length && !newValue.includes(data.value.min_payment_amount_currency_code)) {
    const [firstAvailableOption] = newValue;
    data.value.min_payment_amount_currency_code = firstAvailableOption || '';
  }
});
watch(() => data.value.is_disabled, (newValue) => {
  if (newValue) {
    data.value.hidden = true;
    data.value.export_via_api = false;
  }
});
watch(fileAttachments, (newValue) => {
  if (newValue === 'no') {
    data.value.file_attachment_allowed = false;
    data.value.file_attachment_required = false;
  }
  if (newValue === 'allowed') {
    data.value.file_attachment_allowed = true;
    data.value.file_attachment_required = false;
  }
  if (newValue === 'required') {
    data.value.file_attachment_allowed = true;
    data.value.file_attachment_required = true;
  }
});
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-payment-method-popup"
    :title="id ? $t('settings.paymentMethods.popup.titleEdit') : $t('settings.paymentMethods.popup.title')"
    :width="1140"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="add-payment-method-popup__body">
        <div class="add-payment-method-popup__block t:space-y-16">
          <UiLabel
            :font-size="16"
            :label="$t('settings.paymentMethods.popup.mainInfo')"
            bold
          />
          <UiInput
            v-model="data.name"
            :label="$t('settings.paymentMethods.popup.name')"
            autosize
            size="big"
            :placeholder="$t('settings.paymentMethods.popup.placeholderName')"
            type="text"
            :error="errors.name"
          />
          <div class="t:space-y-8">
            <CardTitle
              :title="$t('settings.paymentMethods.popup.status')"
              :tooltip="$t('settings.paymentMethods.popup.statusHint')"
            >
              <UiSwitch
                v-model="status"
                :status-text="$t(`settings.paymentMethods.statuses.${data.is_disabled ? 'disabled' : 'active'}`)"
                :status-type="data.is_disabled ? 'negative' : 'positive'"
              />
            </CardTitle>
            <CardTitle
              :title="$t('settings.paymentMethods.popup.visibility')"
              :tooltip="$t('settings.paymentMethods.popup.visibilityHint')"
            >
              <UiSwitch
                v-model="visibility"
                :status-text="$t(`settings.paymentMethods.${data.hidden ? 'hidden' : 'visible'}`)"
                :status-type="data.hidden ? 'negative' : 'positive'"
                :disabled="data.is_disabled"
              />
            </CardTitle>
            <CardTitle
              :title="$t('settings.paymentMethods.popup.export')"
            >
              <UiSwitch
                v-model="data.export_via_api"
                :status-text="$t(`settings.paymentMethods.popup.${data.export_via_api ? 'yes' : 'no'}`)"
                :status-type="data.export_via_api ? 'positive' : 'negative'"
                :disabled="data.is_disabled"
              />
            </CardTitle>
            <CardTitle
              :title="$t('settings.paymentMethods.popup.fileAttachments')"
            >
              <UiRadioGroup
                v-model="fileAttachments"
                :options="fileAttachmentsOptions"
              />
            </CardTitle>
          </div>
          <LanguagesSetup
            class="t:-mx-24 t:-mb-16 t:grow"
            :selected-index="index"
            :languages="languages"
            :list-languages="customerLanguages"
            :selected-languages="selectedLanguages"
            :can-make-default="false"
            :errors="mapDescriptionErrors"
            @add-language="handleAddLanguage"
            @select-index="handleSelectTab"
            @delete-language="handleDelete"
          >
            <div class="t:space-y-16">
              <UiTextarea
                v-model="descriptionValue"
                :label="$t('settings.paymentMethods.popup.description')"
                :disabled="!languages.length"
                :placeholder="$t(`settings.paymentMethods.popup.descriptionPlaceholder`)"
                :rows="3"
              />
            </div>
          </LanguagesSetup>
        </div>
        <div class="add-payment-method-popup__block t:space-y-16">
          <UiLabel
            :font-size="16"
            :label="$t('settings.paymentMethods.popup.settings')"
            bold
          />
          <UiRadioGroup
            v-model="data.supported_currencies"
            :label="$t('settings.paymentMethods.popup.currencies')"
            :options="supportedCurrenciesOptions"
          >
            <UiSelectElement
              v-model="currencyCodes"
              :placeholder="$t('settings.paymentMethods.popup.placeholderCurrencies')"
              :list="customerCurrencies"
              :disabled="data.supported_currencies === 'all'"
              search-param="name"
              filterable
              :max-collapse-tags="5"
              collapse-tag-tooltip
              multiple
              option-value-param="code"
              option-label-param="code"
              :error="errors.currency_codes"
              size="big"
            />
          </UiRadioGroup>
          <UiInput
            v-model="data.wallet_regexp"
            :label="$t('settings.paymentMethods.popup.walletRegexp')"
            autosize
            size="big"
            :placeholder="$t('settings.paymentMethods.popup.walletRegexp')"
            type="text"
            :error="errors.wallet_regexp"
          />
          <UiInput
            v-model="data.wallet_placeholder"
            :label="$t('settings.paymentMethods.popup.walletPlaceholder')"
            autosize
            size="big"
            :placeholder="$t('settings.paymentMethods.popup.walletPlaceholder')"
            type="text"
            :error="errors.wallet_placeholder"
          />
          <UiSelectElement
            v-model="data.required_affiliate_fields"
            :placeholder="$t('settings.paymentMethods.popup.notSelected')"
            :label="$t('settings.paymentMethods.popup.requiredAffiliateInfo')"
            :list="requiredAffiliateOptions"
            collapse-tag-tooltip
            multiple
            :error="errors.required_affiliate_fields"
            size="big"
          />
          <UiSelectElement
            v-model="data.min_payment_amount_currency_code"
            class="t:mb-16"
            :placeholder="$t('settings.paymentMethods.popup.notSelected')"
            :label="$t('settings.paymentMethods.popup.minimalPaymentAmountCurrency')"
            :list="minimalPaymentAmountCurrencyOptions"
            size="big"
            option-value-param="code"
            :error="errors.min_payment_amount_currency_code"
          />
          <UiInput
            v-model="minPaymentAmount"
            :label="$t('settings.paymentMethods.popup.minimalPaymentAmount')"
            autosize
            size="big"
            placeholder="0"
            type="text"
            :error="errors.min_payment_amount"
            @change="preventSymbol"
          />
          <UploadFile
            v-model="files"
            :title="$t('settings.paymentMethods.popup.logo')"
            accept=".png, .jpg"
            is-dragger
            :limit-file="1"
            :upload-files-action="uploadFile"
            :remove-file="removeFile"
            :error="errors.files || errors.logo_base64"
          >
            <template v-slot:upload-text>
              <span>{{ $t('settings.paymentMethods.popup.dropFileHereOr') }} <em>{{ $t('settings.paymentMethods.popup.selectFile') }}</em></span>
            </template>
          </UploadFile>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.add-payment-method-popup {
  &__body {
    display: flex;
    margin: -16px -24px;
  }
  &__block {
    width: 50%;
    display: flex;
    flex-direction: column;
    padding: 16px 24px;
    &:first-child {
      border-right: 1px solid var(--border-default);
    }
  }
}
</style>
